import React, { useEffect, useState, useCallback } from 'react';
import { ChevronLeft, Triangle, X } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  PieChart, Pie, Cell, Sector
} from 'recharts';
import logoReport from '../assets/logo_report.png';

// renderActiveShape 함수
const renderActiveShape = (props) => {
  const RADIAN = Math.PI / 180;
  const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
  const cornerRadius = 8;

  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius + 10) * cos;
  const sy = cy + (outerRadius + 10) * sin;
  const mx = cx + (outerRadius + 30) * cos;
  const my = cy + (outerRadius + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
  const ey = my;
  const textAnchor = cos >= 0 ? 'start' : 'end';

  return (
    <g>
      {/* 중앙 텍스트 (이름, 개수, 비율 표시) */}
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fill="#333">
        <tspan x={cx} dy="-0.6em" fontSize={12} fontWeight="bold">{payload.name}</tspan>
        <tspan x={cx} dy="1.2em" fontSize={10}>{`${value}개`}</tspan>
        <tspan x={cx} dy="1.2em" fontSize={10}>{`(${(percent * 100).toFixed(0)}%)`}</tspan>
      </text>
      {/* 기본 섹터 */}
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        cornerRadius={cornerRadius}
      />
      {/* 하이라이트 섹터 */}
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle+5}
        endAngle={endAngle-5}
        innerRadius={outerRadius + 4}
        outerRadius={outerRadius + 8}
        fill={fill}
        cornerRadius={cornerRadius}
      />
    </g>
  );
};

function InsightsReportModal({ isOpen, onClose, reportId = "", ratings = [] }) {
  const [examInfo, setExamInfo] = useState(null);
  const [fieldInfo, setFieldInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [reportError, setReportError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [addressRef, setAddressRef] = useState({});

  // 스크롤바를 숨기는 CSS 스타일 정의
  const hideScrollbarStyle = {
    scrollbarWidth: 'none', // Firefox
    msOverflowStyle: 'none', // IE and Edge
  };

  // --- 파이 차트 상태 추가 --- 
  const [activeIndexLevel, setActiveIndexLevel] = useState(null);
  const [activeIndexDomain, setActiveIndexDomain] = useState(null);

  // --- 파이 차트 이벤트 핸들러 추가 ---
  const onLevelPieEnter = useCallback((_, index) => {
    setActiveIndexLevel(index);
  }, [setActiveIndexLevel]);

  const onDomainPieEnter = useCallback((_, index) => {
    setActiveIndexDomain(index);
  }, [setActiveIndexDomain]);

  // Webkit 스크롤바 숨김 위한 스타일 요소 추가
  useEffect(() => {
    if (!isOpen) return;
    
    const style = document.createElement('style');
    style.textContent = `
      .hide-scrollbar::-webkit-scrollbar {
        display: none;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, [isOpen]);

  // 실제 뷰포트 높이를 계산하여 CSS 변수로 설정
  useEffect(() => {
    if (!isOpen) return;
    
    const setRealViewportHeight = () => {
      // 실제 뷰포트 높이를 픽셀 단위로 계산
      const vh = window.innerHeight * 0.01;
      // 문서의 루트 요소에 CSS 변수로 설정
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    
    // 초기 설정
    setRealViewportHeight();
    
    // 리사이즈 이벤트에 대응하여 업데이트
    window.addEventListener('resize', setRealViewportHeight);
    window.addEventListener('orientationchange', setRealViewportHeight);
    
    // 클린업 함수
    return () => {
      window.removeEventListener('resize', setRealViewportHeight);
      window.removeEventListener('orientationchange', setRealViewportHeight);
    };
  }, [isOpen]);

  // 외부 클릭 이벤트 핸들러
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // 차트 데이터 계산 함수 - props로 받은 ratings 배열 사용
  const getRatingData = useCallback(() => {
    // props로 전달받은 ratings 배열이 있으면 사용하고, 없으면 API 데이터 사용
    if (ratings && ratings.length > 1) {
      return [...ratings].map(rating => ({ value: rating }));
    }
    
    if (!reportData || !reportData.exams || reportData.exams.length === 0) {
      return [];
    }

    // exams 배열에서 rating 값들을 추출 (exam_id 순으로 정렬)
    const sortedExams = [...reportData.exams].sort((a, b) => a.exam_id - b.exam_id);
    return sortedExams.map(exam => ({ value: exam.rating || 0 }));
  }, [reportData, ratings]);

  // Y축 눈금 및 범위 데이터 생성 (동적으로 계산)
  const getYAxisDomain = useCallback(() => {
    // props로 전달받은 ratings 배열이 있으면 사용하고, 없으면 API 데이터 사용
    let ratingArray = [];
    
    if (ratings && ratings.length > 1) {
      ratingArray = ratings;
    } else if (reportData?.exams && reportData.exams.length > 0) {
      ratingArray = reportData.exams.map(exam => exam.rating || 0);
    }
    
    if (ratingArray.length === 0) {
      return [0, 0];
    }
    
    // Insights 컴포넌트와 동일한 방식으로 Y축 범위 계산
    return [
      Math.min(...ratingArray) - Math.max(200, Math.min(...ratingArray) * 0.1), 
      Math.max(...ratingArray) + Math.max(200, Math.max(...ratingArray) * 0.1)
    ];
  }, [reportData, ratings]);

  // 현재 레이팅 값 얻기
  const getCurrentRating = useCallback(() => {
    if (!reportData || !reportData.exams || reportData.exams.length === 0) {
      return 0;
    }
    // exam_id가 가장 높은 것의 rating 사용
    const highestExamIdItem = reportData.exams
      .reduce((max, exam) => exam.exam_id > max.exam_id ? exam : max, reportData.exams[0]);
    return highestExamIdItem?.rating || 0;
  }, [reportData]);

  // 레이팅 변화 계산
  const getRatingChange = useCallback(() => {
    if (!reportData || !reportData.exams || reportData.exams.length < 2) {
      return 0;
    }
    // exams 배열을 exam_id로 정렬하여 최신 두 개의 차이 계산
    const sortedExams = [...reportData.exams].sort((a, b) => b.exam_id - a.exam_id);
    return (sortedExams[0]?.rating || 0) - (sortedExams[1]?.rating || 0);
  }, [reportData]);

  // 정답률 계산 함수
  const getAccuracyData = useCallback(() => {
    if (!reportData || !reportData.recent_record || !reportData.recent_record.by_step) {
      return {
        accuracy: 0,
        correctCount: 0,
        totalCount: 0
      };
    }

    const correctByLevel = reportData.recent_record.by_step[0] || [];
    const incorrectByLevel = reportData.recent_record.by_step[1] || [];
    
    const totalCorrect = correctByLevel.reduce((sum, count) => sum + count, 0);
    const totalIncorrect = incorrectByLevel.reduce((sum, count) => sum + count, 0);
    const totalQuestions = totalCorrect + totalIncorrect;
    const accuracy = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;

    return {
      accuracy,
      correctCount: totalCorrect,
      totalCount: totalQuestions
    };
  }, [reportData]);

  // 누적 정답률 계산 함수
  const getCumulativeAccuracyData = useCallback(() => {
    if (!reportData || !reportData.past_record || !reportData.past_record.by_step) {
      return {
        accuracy: 0,
        correctCount: 0,
        totalCount: 0
      };
    }

    const correctByLevel = reportData.past_record.by_step[0] || [];
    const incorrectByLevel = reportData.past_record.by_step[1] || [];
    
    const totalCorrect = correctByLevel.reduce((sum, count) => sum + count, 0);
    const totalIncorrect = incorrectByLevel.reduce((sum, count) => sum + count, 0);
    const totalQuestions = totalCorrect + totalIncorrect;
    const accuracy = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;

    return {
      accuracy,
      correctCount: totalCorrect,
      totalCount: totalQuestions
    };
  }, [reportData]);

  // 강점 및 약점 데이터 가져오기
  const getStrengthsAndWeaknesses = useCallback(() => {
    if (!reportData || !reportData.opinions) {
      return {
        strengths: [],
        weaknesses: []
      };
    }

    const strengths = [];
    const weaknesses = [];

    if (reportData.opinions.pros) {
      strengths.push({
        description: reportData.opinions.pros
      });
    }

    if (reportData.opinions.cons) {
      weaknesses.push({
        description: reportData.opinions.cons
      });
    }

    return {
      strengths,
      weaknesses
    };
  }, [reportData]);

  // 공부가 필요한 영역 데이터 가져오기
  const getStudyAreas = useCallback(() => {
    if (!reportData || !reportData.opinions || !reportData.opinions.next_step) {
      return [];
    }

    return Object.entries(reportData.opinions.next_step).map(([title, topics]) => ({
      title,
      topics: Array.isArray(topics) ? topics : []
    }));
  }, [reportData]);

  // 학습 조언 데이터 가져오기
  const getLearningGoals = useCallback(() => {
    if (!reportData || !reportData.opinions) {
      return {
        goals: [],
        overallComment: ''
      };
    }

    const comparison = reportData.opinions.comparison || {};
    
    const goals = Object.entries(comparison).map(([title, description]) => ({
      title,
      description,
      period: '1 - 2주'
    }));
    
    const overallComment = reportData.opinions.summary || '';

    return {
      goals,
      overallComment
    };
  }, [reportData]);

  // 유형별 상세 분석 데이터 생성 함수
  const getDetailedAnalysisData = useCallback(() => {
    if (!reportData || !reportData.recent_record || !reportData.past_record) {
      return [];
    }

    const recentData = reportData.recent_record.by_address || {};
    const pastData = reportData.past_record.by_address || {};

    const allAddressKeys = [...new Set([
      ...Object.keys(recentData),
      ...Object.keys(pastData)
    ])];

    return allAddressKeys.map(address => {
      const recent = recentData[address] || [0, 0];
      const past = pastData[address] || [0, 0];

      const recentCorrect = recent[0] || 0;
      const recentIncorrect = recent[1] || 0;
      const recentTotal = recentCorrect + recentIncorrect;
      
      const pastCorrect = past[0] || 0;
      const pastIncorrect = past[1] || 0;
      const pastTotal = pastCorrect + pastIncorrect;

      const recentAccuracy = recentTotal > 0 
        ? `${Math.round((recentCorrect / recentTotal) * 100)}% (${recentCorrect}/${recentTotal})`
        : '0% (0/0)';
      
      const cumulativeAccuracy = pastTotal > 0 
        ? `${Math.round((pastCorrect / pastTotal) * 100)}% (${pastCorrect}/${pastTotal})`
        : '0% (0/0)';

      const totalCorrect = recentCorrect + pastCorrect;
      const totalIncorrect = recentIncorrect + pastIncorrect;
      
      const correctDots = Array(totalCorrect).fill('correct');
      const incorrectDots = Array(totalIncorrect).fill('incorrect');
      const allDots = [...correctDots, ...incorrectDots];

      // addressRef를 사용하여 실제 이름 가져오기
      const subCategory = addressRef[address] || address;

      return {
        address,
        subCategory,
        accuracy: recentAccuracy,
        cumulativeAccuracy,
        correctDots,
        incorrectDots,
        allDots,
        category: address.split('/').slice(0, 2).join('/')
      };
    }).sort((a, b) => a.category.localeCompare(b.category));
  }, [reportData, addressRef]);

  // 유형별 도넛 차트 데이터 생성 함수 (영역별)
  const getDomainDonutData = useCallback(() => {
    if (!reportData) {
      return [];
    }

    const recentData = reportData.recent_record?.by_address || {};
    const pastData = reportData.past_record?.by_address || {};

    const allAddressKeys = [...new Set([
      ...Object.keys(recentData),
      ...Object.keys(pastData)
    ])];

    const subjectMap = new Map();

    allAddressKeys.forEach(address => {
      const recent = recentData[address] || [0, 0];
      const past = pastData[address] || [0, 0];

      const totalCount = (recent[0] + recent[1]) + (past[0] + past[1]);
      
      // addressRef를 사용하여 실제 이름 가져오기
      const subjectName = addressRef[address] || address;
      
      if (!subjectMap.has(subjectName)) {
        subjectMap.set(subjectName, 0);
      }
      
      subjectMap.set(subjectName, subjectMap.get(subjectName) + totalCount);
    });

    return Array.from(subjectMap.entries())
      .map(([name, value]) => ({ name, value }))
      .filter(item => item.value > 0);
  }, [reportData, addressRef]);

  // 유형별 레이더 차트 데이터 생성 함수
  const getSubjectRadarData = useCallback(() => {
    if (!reportData) {
      return [];
    }

    const recentData = reportData.recent_record?.by_address || {};
    const pastData = reportData.past_record?.by_address || {};

    const allAddressKeys = [...new Set([
      ...Object.keys(recentData),
      ...Object.keys(pastData)
    ])];

    const subjectMap = new Map();

    allAddressKeys.forEach(address => {
      const recent = recentData[address] || [0, 0];
      const past = pastData[address] || [0, 0];

      const totalCorrect = recent[0] + past[0];
      const totalIncorrect = recent[1] + past[1];
      const totalCount = totalCorrect + totalIncorrect;
      
      const accuracy = totalCount > 0 ? Math.round((totalCorrect / totalCount) * 100) : 0;
      
      // addressRef를 사용하여 실제 이름 가져오기
      const subjectName = addressRef[address] || address;
      
      subjectMap.set(subjectName, accuracy);
    });

    return Array.from(subjectMap.entries())
      .map(([subject, A]) => ({
        subject,
        A,
        fullMark: 100
      }));
  }, [reportData, addressRef]);

  // 난이도별 정답률 계산 함수
  const getLevelAchievementData = useCallback(() => {
    if (!reportData || !reportData.recent_record || !reportData.recent_record.by_step || 
        !reportData.past_record || !reportData.past_record.by_step) {
      return [
        { level: 1, currentAcc: 0, currentCorrect: 0, currentTotal: 0, cumulativeAcc: 0, cumulativeCorrect: 0, cumulativeTotal: 0 },
        { level: 2, currentAcc: 0, currentCorrect: 0, currentTotal: 0, cumulativeAcc: 0, cumulativeCorrect: 0, cumulativeTotal: 0 },
        { level: 3, currentAcc: 0, currentCorrect: 0, currentTotal: 0, cumulativeAcc: 0, cumulativeCorrect: 0, cumulativeTotal: 0 },
        { level: 4, currentAcc: 0, currentCorrect: 0, currentTotal: 0, cumulativeAcc: 0, cumulativeCorrect: 0, cumulativeTotal: 0 },
        { level: 5, currentAcc: 0, currentCorrect: 0, currentTotal: 0, cumulativeAcc: 0, cumulativeCorrect: 0, cumulativeTotal: 0 },
      ];
    }

    const recentCorrect = reportData.recent_record.by_step[0] || [0, 0, 0, 0, 0];
    const recentIncorrect = reportData.recent_record.by_step[1] || [0, 0, 0, 0, 0];

    const pastCorrect = reportData.past_record.by_step[0] || [0, 0, 0, 0, 0];
    const pastIncorrect = reportData.past_record.by_step[1] || [0, 0, 0, 0, 0];

    return [1, 2, 3, 4, 5].map((level, index) => {
      const currentCorrect = recentCorrect[index] || 0;
      const currentIncorrect = recentIncorrect[index] || 0;
      const currentTotal = currentCorrect + currentIncorrect;
      const currentAcc = currentTotal > 0 ? Math.round((currentCorrect / currentTotal) * 100) : 0;

      const cumulativeCorrect = pastCorrect[index] || 0;
      const cumulativeIncorrect = pastIncorrect[index] || 0;
      const cumulativeTotal = cumulativeCorrect + cumulativeIncorrect;
      const cumulativeAcc = cumulativeTotal > 0 ? Math.round((cumulativeCorrect / cumulativeTotal) * 100) : 0;

      return {
        level,
        currentAcc,
        currentCorrect,
        currentTotal,
        cumulativeAcc,
        cumulativeCorrect,
        cumulativeTotal
      };
    });
  }, [reportData]);

  // 평균 난이도 계산 함수
  const getAverageDifficulty = useCallback(() => {
    if (!reportData || !reportData.exams || reportData.exams.length === 0) {
      return 0;
    }
    // exam_id가 가장 높은 시험의 난이도 사용
    const highestExamIdItem = reportData.exams
      .reduce((max, exam) => exam.exam_id > max.exam_id ? exam : max, reportData.exams[0]);
    return (highestExamIdItem?.difficulty || 0) / 100;
  }, [reportData]);

  // 누적 난이도 계산 함수
  const getCumulativeDifficulty = useCallback(() => {
    if (!reportData || !reportData.past_avg_difficulty) {
      return 0;
    }
    return reportData.past_avg_difficulty / 100;
  }, [reportData]);

  // 난이도 레벨을 텍스트로 변환하는 함수
  const getDifficultyLevelText = useCallback((difficulty) => {
    if (difficulty < 1.5) return "하";
    if (difficulty < 2.5) return "중하";
    if (difficulty < 3.5) return "중";
    if (difficulty < 4.5) return "중상";
    return "상";
  }, []);

  // 난이도별 도넛 차트 데이터 생성 함수
  const getLevelDonutData = useCallback(() => {
    if (!reportData || !reportData.recent_record || !reportData.past_record) {
      return [];
    }

    const recentCorrect = reportData.recent_record.by_step?.[0] || [0, 0, 0, 0, 0];
    const recentIncorrect = reportData.recent_record.by_step?.[1] || [0, 0, 0, 0, 0];
    const pastCorrect = reportData.past_record.by_step?.[0] || [0, 0, 0, 0, 0];
    const pastIncorrect = reportData.past_record.by_step?.[1] || [0, 0, 0, 0, 0];

    const totalCorrect = recentCorrect.map((count, i) => count + pastCorrect[i]);
    const totalIncorrect = recentIncorrect.map((count, i) => count + pastIncorrect[i]);
    
    const totalQuestions = totalCorrect.map((count, i) => count + totalIncorrect[i]);

    return [1, 2, 3, 4, 5].map((level, index) => ({
      name: `LV ${level}`,
      value: totalQuestions[index] || 0
    })).filter(item => item.value > 0);
  }, [reportData]);

  // 난이도별 레이더 차트 데이터 생성 함수
  const levelRadarData = useCallback(() => {
    if (!reportData || !reportData.recent_record || !reportData.past_record) {
      return [];
    }

    const recentCorrect = reportData.recent_record.by_step?.[0] || [0, 0, 0, 0, 0];
    const recentIncorrect = reportData.recent_record.by_step?.[1] || [0, 0, 0, 0, 0];
    const pastCorrect = reportData.past_record.by_step?.[0] || [0, 0, 0, 0, 0];
    const pastIncorrect = reportData.past_record.by_step?.[1] || [0, 0, 0, 0, 0];

    const totalCorrect = recentCorrect.map((count, i) => count + pastCorrect[i]);
    const totalIncorrect = recentIncorrect.map((count, i) => count + pastIncorrect[i]);
    
    return [1, 2, 3, 4, 5].map((level, index) => {
      const correct = totalCorrect[index] || 0;
      const incorrect = totalIncorrect[index] || 0;
      const total = correct + incorrect;
      const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;

      return {
        subject: `LV ${level}`,
        A: accuracy,
        fullMark: 100,
      };
    });
  }, [reportData]);

  // 데이터 로드
  useEffect(() => {
    if (!isOpen) return;
    
    let isMounted = true;
    setIsLoading(true);

    // sessionStorage에서 데이터 로드
    const storedReportData = sessionStorage.getItem('selectedReportData');
    const storedAddressRef = sessionStorage.getItem('addressRef');
    
    if (storedReportData) {
      try {
        const parsedData = JSON.parse(storedReportData);
        setReportData(parsedData);
        
        if (storedAddressRef) {
          setAddressRef(JSON.parse(storedAddressRef));
        }
        
        // 시험 정보 설정 (exam_id가 가장 높은 exam 사용)
        if (parsedData?.exams && parsedData.exams.length > 0) {
          // exam_id가 가장 높은 시험 찾기
          const latestExam = parsedData.exams
            .reduce((max, exam) => exam.exam_id > max.exam_id ? exam : max, parsedData.exams[0]);
          const examInfoData = {
            exam_type: latestExam.exam_type || '기타',
            updated_at: latestExam.updated_at || new Date().toISOString(),
            exam_count: latestExam.exam_round || 1,
            exam_id: latestExam.exam_id || reportId,
            difficulty: latestExam.difficulty || 0,
            length: latestExam.length || 0,
          };
          
          setExamInfo(examInfoData);
        }
        
        // 과목명(tree_name) 설정
        const fieldInfoData = {
          tree_name: parsedData.tree_name || '과목 정보 없음'
        };
        
        setFieldInfo(fieldInfoData);
        setReportError(false);
      } catch (error) {
        console.error('저장된 데이터 파싱 에러:', error);
        setReportError(true);
        setErrorMessage('데이터를 불러오는데 실패했습니다.');
      }
    } else {
      setReportError(true);
      setErrorMessage('리포트 데이터가 없습니다.');
    }
    
    setIsLoading(false);

    return () => {
      isMounted = false;
    };
  }, [isOpen, reportId]);

  const formatExamDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const year = date.getFullYear().toString().slice(2);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}.${month}.${day}`;
    } catch (e) {
      console.error("Invalid date format:", dateString);
      return '';
    }
  };

  // 모달이 닫혀있으면 아무것도 렌더링하지 않음
  if (!isOpen) return null;

  // 도넛 차트 색상 (초록색 계열)
  const LEVEL_GREEN_COLORS = ['#36916E', '#4ABC85', '#7ED0A5', '#A2E4C1', '#C5F1D8'];
  const DOMAIN_GREEN_COLORS = ['#36916E', '#4ABC85', '#7ED0A5', '#A2E4C1', '#C5F1D8', '#2C735A', '#5ED1A0', '#8BE7BF'];

  // 난이도별/영역별 차트 범례 데이터
  const levelLegend = [
    { name: 'LV 1', color: LEVEL_GREEN_COLORS[0] }, 
    { name: 'LV 2', color: LEVEL_GREEN_COLORS[1] },
    { name: 'LV 3', color: LEVEL_GREEN_COLORS[2] }, 
    { name: 'LV 4', color: LEVEL_GREEN_COLORS[3] },
    { name: 'LV 5', color: LEVEL_GREEN_COLORS[4] }
  ];

  // 로딩 중 화면
  if (isLoading) return (
    <div className="fixed inset-0 bg-black/15 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-8 max-w-md">
        <div className="flex flex-col items-center justify-center">
          <div className="w-12 h-12 border-4 border-gray-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-700 font-medium animate-pulse">로딩중...</p>
        </div>
      </div>
    </div>
  );

  // 에러 상태일 때 표시할 UI
  if (reportError) return (
    <div className="fixed inset-0 bg-black/15 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-8 max-w-md">
        <div className="flex flex-col items-center justify-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <span className="text-[#4ABC85] text-2xl">!</span>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">리포트를 준비 중입니다.</h2>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-[#4ABC85] text-white rounded-lg hover:bg-[#3da575] mt-4"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* 전체 화면 배경 오버레이 */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose}></div>
      
      {/* 모달 콘텐츠 컨테이너 */}
      <div className="fixed inset-0 flex flex-col items-center z-50 overflow-hidden" onClick={handleBackdropClick} style={{ height: 'calc(var(--vh, 1vh) * 100)' }}>
        <div className="w-full max-w-[316px] mx-auto h-full flex flex-col py-5" onClick={(e) => e.stopPropagation()}>
          {/* 상단 여백 */}
          <div className="h-5 flex-shrink-0"></div>
          
          {/* 모달 컨테이너 */}
          <div className="bg-[#fafafa] rounded-2xl w-full flex flex-col flex-grow overflow-hidden">
            {/* 헤더 영역 - 제목과 닫기 버튼 */}
            <div className="sticky top-0 flex justify-between items-center px-4 pt-4 z-50 bg-[#fafafa] rounded-t-2xl">
              {/* AI 리포트 제목 (왼쪽 정렬) */}
              <div className="flex items-center">
                <h1 className="text-[18px] font-black text-[#4ABC85]">AI</h1>
                <h1 className="text-[18px] font-black text-[#231F20] pl-1">리포트</h1>
              </div>
              
              {/* 닫기 버튼 (오른쪽 정렬) */}
              <button 
                onClick={onClose} 
                className="p-1 rounded-full bg-white shadow-md"
              >
                <X size={20} className="text-[#231F20]" />
              </button>
            </div>

            {/* 스크롤 가능한 콘텐츠 영역 */}
            <div className="flex-grow overflow-y-auto hide-scrollbar pb-4" style={hideScrollbarStyle}>
              <div className="flex flex-col items-center pt-[10px] px-4">
                <div className="w-full max-w-[316px]">
                  {/* 시험 정보 카드 시작 */}
                  <div className="w-full rounded-2xl p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] mb-[10px] bg-white">
                    {examInfo && fieldInfo ? (
                      <div className="flex flex-col">
                        <div className="flex justify-between items-center mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-[14px] font-semibold text-[#231F20]">
                              {fieldInfo.tree_name || '과목명'}
                            </span>
                            <span className="w-[38px] h-[16px] flex items-center justify-center bg-[#4ABC85] text-[#FFFFFF] rounded-full text-[8px]">
                              {examInfo.exam_type}
                            </span>
                          </div>
                          <span className="text-[12px] text-[#A3A1A2]">
                            {formatExamDate(examInfo.updated_at)}
                          </span>
                        </div>

                        <div className="w-full bg-[#f1f1f1] rounded-[16px] flex items-center justify-center px-4 h-[50px]">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1">
                              <span className="text-[#231F20]/60 text-[11px]">회차</span>
                              <span className="text-[#231F20] text-[11px]">{examInfo.exam_count}</span>
                            </div>
                            <div className="h-3 w-[1px] bg-[#cccccc]"></div>
                            <div className="flex items-center gap-1">
                              <span className="text-[#231F20]/60 text-[11px]">난이도</span>
                              <span className="text-[#231F20] font-medium text-[11px]">{(examInfo.difficulty / 100).toFixed(2)}</span>
                            </div>
                            <div className="h-3 w-[1px] bg-[#cccccc]"></div>
                            <div className="flex items-center gap-1">
                              <span className="text-[#231F20]/60 text-[11px]">문제 수</span>
                              <span className="text-[#231F20] font-medium text-[11px]">{examInfo.length}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="h-[72px] flex items-center justify-center">
                        <p className="text-gray-500">시험 정보 로딩 중...</p>
                      </div>
                    )}
                  </div>
                  {/* 시험 정보 카드 끝 */}

                  {/* 테스트 성적 요약 카드 시작 */}
                  <div className="w-full rounded-2xl p-6 shadow-[0_0_6px_rgba(0,0,0,0.1)] bg-white mb-8">
                    <h2 className="text-base font-bold mb-5 text-[#231F20]">테스트 성적 요약</h2>

                    {/* 레이팅, 정답률, 난이도 섹션 - 2열 그리드로 변경 */}
                    <div className="grid grid-cols-2 gap-3 mb-6">

                      {/* 항목 1: 현재 레이팅 (2열 차지) */}
                      <div className="bg-[#F8F8F8] rounded-lg p-3 flex flex-col items-center col-span-2">
                        <span className="text-[14px] font-semibold text-[#231F20] mb-1">현재 폼레이팅</span>
                        <p className="text-[28px] font-bold text-[#4ABC85] mb-2">{getCurrentRating().toLocaleString()}</p>
                        <div className="flex items-center">
                          <div className="flex items-center border border-[#D9D9D9] rounded-lg h-[20px] px-2">
                            <Triangle 
                              className={`w-2 h-2 mr-0.5 fill-current ${
                                getRatingChange() < 0 
                                  ? 'text-blue-600 rotate-180' 
                                  : 'text-red-600 rotate-0'
                              }`} 
                            />
                            <div className="flex items-baseline">
                              <span className="text-[10px] font-semibold text-[#231F20]">{Math.abs(getRatingChange())}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 항목 2: 정답률 */}
                      <div className="bg-[#F8F8F8] rounded-lg p-3 flex flex-col items-center">
                        <span className="text-[14px] font-semibold text-[#231F20] mb-1">정답률</span>
                        <p className="text-[28px] font-bold text-[#231F20] mb-2">{getAccuracyData().accuracy}<span className="text-[18px] align-baseline">%</span></p>
                        <div className="flex items-center">
                          <div className="flex items-center border border-[#D9D9D9] rounded-lg h-[20px] px-2">
                            <div className="flex items-baseline">
                              <span className="text-[10px] font-semibold text-[#231F20]">{getAccuracyData().correctCount}/{getAccuracyData().totalCount}</span>
                              <span className="text-[6px] font-semibold text-[#231F20] ml-0.5">문제</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 항목 3: 누적 정답률 */}
                      <div className="bg-[#F8F8F8] rounded-lg p-3 flex flex-col items-center">
                        <span className="text-[14px] font-semibold text-[#231F20] mb-1">누적 정답률</span>
                        <p className="text-[28px] font-bold text-[#231F20] mb-2">{getCumulativeAccuracyData().accuracy}<span className="text-[18px] align-baseline">%</span></p>
                        <div className="flex items-center">
                          <div className="flex items-center border border-[#D9D9D9] rounded-lg h-[20px] px-2">
                            <div className="flex items-baseline">
                              <span className="text-[10px] font-semibold text-[#231F20]">{getCumulativeAccuracyData().correctCount}/{getCumulativeAccuracyData().totalCount}</span>
                              <span className="text-[6px] font-semibold text-[#231F20] ml-0.5">문제</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 항목 4: 평균 난이도 - 추가 */}
                      <div className="bg-[#F8F8F8] rounded-lg p-3 flex flex-col items-center">
                        <span className="text-[14px] font-semibold text-[#231F20] mb-1">평균 난이도</span>
                        <p className="text-[28px] font-bold text-[#231F20] mb-2">{getAverageDifficulty().toFixed(2)}</p>
                        <div className="flex items-center">
                          <div className="flex items-center border border-[#D9D9D9] rounded-lg h-[20px] px-2">
                            <div className="flex items-baseline">
                              <span className="text-[10px] font-semibold text-[#231F20]">{getDifficultyLevelText(getAverageDifficulty())}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 항목 5: 누적 난이도 - 추가 */}
                      <div className="bg-[#F8F8F8] rounded-lg p-3 flex flex-col items-center">
                        <span className="text-[14px] font-semibold text-[#231F20] mb-1">누적 난이도</span>
                        <p className="text-[28px] font-bold text-[#231F20] mb-2">{getCumulativeDifficulty().toFixed(2)}</p>
                        <div className="flex items-center">
                          <div className="flex items-center border border-[#D9D9D9] rounded-lg h-[20px] px-2">
                            <div className="flex items-baseline">
                              <span className="text-[10px] font-semibold text-[#231F20]">{getDifficultyLevelText(getCumulativeDifficulty())}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 레이팅 변화 추이 그래프 */}
                      <div className="col-span-2">
                        <h3 className="text-[14px] font-bold mb-3 text-[#231F20]">폼레이팅 변화 추이</h3>
                        <div className="relative h-44 mb-2">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                              data={getRatingData()}
                              margin={{ top: 5, right: 10, left: -45, bottom: 5 }}
                            >
                              <YAxis
                                axisLine={false}
                                tickLine={false}
                                domain={getYAxisDomain()} 
                                tick={false}
                              />
                              <Line
                                type="monotone"
                                dataKey="value"
                                stroke="#4ABC85"
                                strokeWidth={1.5}
                                dot={{ r: 2.5, fill: '#4ABC85' }}
                                activeDot={{ r: 4 }}
                                isAnimationActive={false}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* 테스트 성적 요약 카드 끝 */}
                  
                  {/* 난이도별 상세 분석 카드 시작 */}
                  <div className="w-full rounded-2xl p-6 shadow-[0_0_6px_rgba(0,0,0,0.1)] bg-white mb-8">
                    <h2 className="text-base font-bold mb-5 text-[#231F20]">난이도별 상세 분석</h2>
                    
                    {/* 난이도별 정답률 섹션 (막대 그래프) */}
                    <div className="mb-6">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="text-[14px] font-bold text-[#231F20]">난이도별 정답률</h3>
                        {/* 범례 추가 */}
                        <div className="flex space-x-3">
                          <div className="flex items-center">
                            <div className="w-2.5 h-2.5 bg-[#4ABC85] rounded-sm mr-1"></div>
                            <span className="text-[10px] text-[#A3A1A2]">현재 테스트</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-2.5 h-2.5 bg-[#A3A1A2] rounded-sm mr-1"></div>
                            <span className="text-[10px] text-[#A3A1A2]">누적 테스트</span>
                          </div>
                        </div>
                      </div>
                      {/* 레벨별 데이터 표시 (구조 변경) */}
                      <div className="flex gap-2">
                        {/* 왼쪽 회색 박스 (레벨 + 막대 + 퍼센트) */}
                        <div className="flex-1 bg-[#F8F8F8] rounded-lg p-4 space-y-4">
                          {getLevelAchievementData().map(item => (
                            <div key={item.level} className="grid grid-cols-[auto_1fr] items-center gap-x-2 text-[12px]">
                              {/* 레벨 표시 */}
                              <span className="w-10 font-medium text-[#231F20]">LV {item.level}</span>
                              
                              {/* 진행률 바 + 퍼센트 */}
                              <div className="space-y-1.5">
                                {/* 현재 테스트 바 */}
                                <div className="flex items-center">
                                  <div className="flex-grow bg-[#F1F1F1] rounded-full h-[10px] overflow-hidden mr-2">
                                    <div
                                      className="bg-[#4ABC85] h-full rounded-full"
                                      style={{ width: `${item.currentAcc}%` }}
                                    ></div>
                                  </div>
                                  <span className="w-8 text-right text-[#231F20]">{item.currentAcc}%</span>
                                </div>
                                {/* 누적 테스트 바 - 색상 변경 */}
                                <div className="flex items-center">
                                  <div className="flex-grow bg-[#F1F1F1] rounded-full h-[10px] overflow-hidden mr-2">
                                    <div
                                      className="bg-[#A3A1A2] h-full rounded-full"
                                      style={{ width: `${item.cumulativeAcc}%` }}
                                    ></div>
                                  </div>
                                  <span className="w-8 text-right text-[#A3A1A2]">{item.cumulativeAcc}%</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* 오른쪽 회색 박스 (개수) - 분자/분모 색상 분리 */}
                        <div className="bg-[#F8F8F8] rounded-lg p-4 space-y-[18px]">
                          {getLevelAchievementData().map(item => (
                            <div key={item.level} className="text-right space-y-1 text-[12px]">
                              {/* 현재 개수: 분자(초록)/분모(검정) */}
                              <div className="block font-semibold">
                                <span className="text-[#4ABC85]">{item.currentCorrect}</span>
                                <span className="text-[#231F20]">/{item.currentTotal}</span>
                              </div>
                              {/* 누적 개수: 분자(회색)/분모(검정) */}
                              <div className="block font-semibold">
                                <span className="text-[#A3A1A2]">{item.cumulativeCorrect}</span>
                                <span className="text-[#231F20]">/{item.cumulativeTotal}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* 난이도별 성취도 (도넛 차트) */}
                    <div className="mb-6">
                      <h3 className="text-[14px] font-bold mb-3 text-[#231F20]">난이도별 성취도</h3>
                      <div className="h-48 mb-2">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              activeIndex={activeIndexLevel}
                              activeShape={renderActiveShape}
                              data={getLevelDonutData()}
                              cx="50%"
                              cy="50%"
                              innerRadius="60%"
                              outerRadius="80%"
                              paddingAngle={5}
                              cornerRadius={8}
                              dataKey="value"
                              onMouseEnter={onLevelPieEnter}
                            >
                              {getLevelDonutData().map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={LEVEL_GREEN_COLORS[index % LEVEL_GREEN_COLORS.length]} />
                              ))}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      {/* 범례 */} 
                      <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center">
                        {levelLegend.map((legend, index) => (
                          <div key={legend.name} className="flex items-center">
                            <div className="w-2.5 h-2.5 rounded-sm mr-1" style={{ backgroundColor: LEVEL_GREEN_COLORS[index % LEVEL_GREEN_COLORS.length] }}></div>
                            <span className="text-[10px] text-[#7B7979]">{legend.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* 난이도별 정답률 (레이더 차트) */}
                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="text-[14px] font-bold text-[#231F20]">난이도별 정답률</h3>
                        <div className="flex items-center">
                          <div className="w-3 h-3 bg-[#4ABC85] mr-1 rounded-sm"></div>
                          <span className="text-[10px] text-[#7B7979]">정답률 (%)</span>
                        </div>
                      </div>
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <RadarChart cx="50%" cy="50%" outerRadius="70%" data={levelRadarData()}>
                            <PolarGrid stroke="#E0E0E0" />
                            <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: '#7B7979' }} />
                            <PolarRadiusAxis angle={30} domain={[0, 100]} axisLine={false} tick={false} />
                            <Radar name="정답률" dataKey="A" stroke="#4ABC85" fill="#4ABC85" fillOpacity={0.6} />
                            <Tooltip
                              contentStyle={{ fontSize: '10px', padding: '2px 5px', borderRadius: '4px', border: '1px solid #ccc' }}
                              formatter={(value, name, props) => [`${props.payload.subject}: ${value}%`, null]}
                            />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                  {/* 난이도별 상세 분석 카드 끝 */}

                  {/* 유형별 상세 분석 카드 시작 */}
                  <div className="w-full rounded-2xl p-6 shadow-[0_0_6px_rgba(0,0,0,0.1)] bg-white mb-8">
                    <h2 className="text-base font-bold mb-5 text-[#231F20]">유형별 상세 분석</h2>

                    {/* 테이블 제목 */}
                    <h3 className="text-[14px] font-bold mb-3 text-[#231F20]">유형별 정답률</h3>

                    {/* 횡스크롤 테이블 영역 */}
                    <div className="mb-6 pb-2">
                      <div className="overflow-x-auto hide-scrollbar" style={{ scrollbarWidth: 'none', ...hideScrollbarStyle }}>
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="py-2 px-2 text-left font-medium text-[#7B7979] w-[150px] whitespace-nowrap">소단원</th>
                              <th className="py-2 px-2 text-left font-medium text-[#7B7979] w-[100px] whitespace-nowrap">정답률</th>
                              <th className="py-2 px-2 text-left font-medium text-[#7B7979] w-[100px] whitespace-nowrap">누적 정답률</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getDetailedAnalysisData().map((item, index) => {
                              // 정답률 문자열 파싱 ('XX% (Y/Z)' 형식)
                              const accuracyMatch = item.accuracy.match(/(\d+)%\s*\((\d+)\/(\d+)\)/);
                              const cumulativeMatch = item.cumulativeAccuracy.match(/(\d+)%\s*\((\d+)\/(\d+)\)/);
                              
                              return (
                                <tr key={index} className={`${index === getDetailedAnalysisData().length - 1 ? '' : 'border-b'} border-gray-200`}>
                                  <td className="py-3 px-2 align-middle text-[#231F20] whitespace-nowrap overflow-hidden text-ellipsis" style={{ maxWidth: '150px' }}>
                                    {item.subCategory}
                                  </td>
                                  <td className="py-3 px-2 align-middle whitespace-nowrap font-bold">
                                    {accuracyMatch ? (
                                      <>
                                        <span className="text-[#4ABC85]">{accuracyMatch[2]}</span>
                                        <span className="text-[#231F20]">/{accuracyMatch[3]}</span>
                                        <span className="ml-1 text-[#7B7979] text-[10px]">({accuracyMatch[1]}%)</span>
                                      </>
                                    ) : (
                                      item.accuracy
                                    )}
                                  </td>
                                  <td className="py-3 px-2 align-middle whitespace-nowrap font-bold">
                                    {cumulativeMatch ? (
                                      <>
                                        <span className="text-[#A3A1A2]">{cumulativeMatch[2]}</span>
                                        <span className="text-[#231F20]">/{cumulativeMatch[3]}</span>
                                        <span className="ml-1 text-[#7B7979] text-[10px]">({cumulativeMatch[1]}%)</span>
                                      </>
                                    ) : (
                                      item.cumulativeAccuracy
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* 영역별 성취도 (도넛 차트) */} 
                    <div className="mb-6">
                      <h3 className="text-[14px] font-bold mb-3 text-[#231F20]">유형별 성취도</h3>
                      <div className="h-48 mb-2">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              activeIndex={activeIndexDomain}
                              activeShape={renderActiveShape}
                              data={getDomainDonutData()}
                              cx="50%"
                              cy="50%"
                              innerRadius="60%"
                              outerRadius="80%"
                              cornerRadius={8}
                              paddingAngle={5}
                              dataKey="value"
                              onMouseEnter={onDomainPieEnter}
                            >
                              {getDomainDonutData().map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={DOMAIN_GREEN_COLORS[index % DOMAIN_GREEN_COLORS.length]} />
                              ))}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      {/* 범례 */} 
                      <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center">
                        {getDomainDonutData().map((item, index) => (
                          <div key={item.name} className="flex items-center">
                            <div className="w-2.5 h-2.5 rounded-sm mr-1" style={{ backgroundColor: DOMAIN_GREEN_COLORS[index % DOMAIN_GREEN_COLORS.length] }}></div>
                            <span className="text-[10px] text-[#7B7979]">{item.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* 영역별 정답률 섹션 (레이더 차트) */} 
                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="text-[14px] font-bold text-[#231F20]">주요 유형별 정답률</h3>
                        <div className="flex items-center">
                          <div className="w-3 h-3 bg-[#4ABC85] mr-1 rounded-sm"></div>
                          <span className="text-[10px] text-[#7B7979]">정답률 (%)</span>
                        </div>
                      </div>
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <RadarChart cx="50%" cy="50%" outerRadius="70%" data={getSubjectRadarData()}>
                            <PolarGrid stroke="#E0E0E0" />
                            <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: '#7B7979' }} />
                            <PolarRadiusAxis angle={30} domain={[0, 100]} axisLine={false} tick={false} />
                            <Radar name="정답률" dataKey="A" stroke="#4ABC85" fill="#4ABC85" fillOpacity={0.6} />
                            <Tooltip
                              contentStyle={{ fontSize: '10px', padding: '2px 5px', borderRadius: '4px', border: '1px solid #ccc' }}
                              formatter={(value, name, props) => [`${props.payload.subject}: ${value}%`, null]}
                            />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                  {/* 유형별 상세 분석 카드 끝 */}

                </div>
              </div>
            </div>
          </div>

          {/* 중간 여백 */}
          <div className="h-10 flex-shrink-0"></div>
          
          {/* 버튼 영역 */}
          <div className="w-full flex-shrink-0 h-[60px]">
            <a 
              href="https://walla.my/v/rIDK7IHx9jXtJulYl9cc" 
              className="w-full h-full flex items-center justify-center bg-[#4ABC85] text-white font-bold rounded-2xl shadow-md hover:bg-[#3da575] transition-colors"
              rel="noopener noreferrer"
              target="_blank"
            >
              상담 예약하기
            </a>
          </div>
          
          {/* 하단 여백 */}
          <div className="h-5 flex-shrink-0"></div>
        </div>
      </div>
    </>
  );
}

export default InsightsReportModal;
